import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Trash2, Upload } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Banner';
import { Skeleton } from '@/components/ui/Skeleton';
import { Input, Label } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { useAuthStore } from '@/stores/auth';
import { configApi, type OrganizationProfile } from '@/services/config.api';
import { prospectsApi } from '@/services/prospects.api';
import { applyBrandColor } from '@/hooks/useBrandBootstrap';
import { cn } from '@/lib/utils';

/**
 * Onglet « Entreprise » de la page Config - admin uniquement côté UI (gaté
 * par le router backend également : `authorize(['admin'])` sur les mutations).
 *
 * UX : tout est en staging - SIRET / ORIAS / adresse / couleur / alignement /
 * images. L'utilisateur visualise ses changements (preview Blob URL pour les
 * images) et ne persiste qu'au clic « Enregistrer les modifications ».
 */
export function EnterpriseTab({ userRole }: { userRole?: string }) {
  const isAdmin = userRole === 'admin';
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const confirm = useConfirm();

  const [profile, setProfile] = useState<OrganizationProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Champs de formulaire - copie éditable du profil, reset via `load()`.
  const [name, setName] = useState('');
  const [siret, setSiret] = useState('');
  const [orias, setOrias] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [brandColor, setBrandColor] = useState('#3E63DD');
  const [logoAlign, setLogoAlign] = useState<'left' | 'center'>('left');
  const [inseeLoading, setInseeLoading] = useState(false);

  // Fichiers en attente d'upload - non persistés en BDD tant que le user ne
  // clique pas « Enregistrer ». Preview générée via URL.createObjectURL.
  const [pendingLogo, setPendingLogo] = useState<File | null>(null);
  const [pendingFavicon, setPendingFavicon] = useState<File | null>(null);
  // Flags « supprimer à l'enregistrement » : l'utilisateur clique Supprimer
  // sur un asset existant → on marque mais on ne touche pas la BDD avant save.
  const [logoDeletionPending, setLogoDeletionPending] = useState(false);
  const [faviconDeletionPending, setFaviconDeletionPending] = useState(false);

  // URL courante de l'asset serveur (Blob URL fetché au mount) - pour afficher
  // la preview avant tout changement. Distinct du pending (File local).
  const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(null);
  const [currentFaviconUrl, setCurrentFaviconUrl] = useState<string | null>(null);

  async function load() {
    try {
      const p = await configApi.getOrganization(token);
      setProfile(p);
      setName(p.name ?? '');
      setSiret(p.siret ?? '');
      setOrias(p.orias ?? '');
      setStreet(p.address?.street ?? '');
      setCity(p.address?.city ?? '');
      setZip(p.address?.zip ?? '');
      setBrandColor(p.brandColor ?? '#3E63DD');
      setLogoAlign(p.logoAlign ?? 'left');
      setPendingLogo(null);
      setPendingFavicon(null);
      setLogoDeletionPending(false);
      setFaviconDeletionPending(false);
      // Fetch des Blob URLs pour les assets actuels (nécessite auth).
      if (p.hasLogo) {
        void fetchAsset('logo', token).then(setCurrentLogoUrl);
      } else {
        setCurrentLogoUrl(null);
      }
      if (p.hasFavicon) {
        void fetchAsset('favicon', token).then(setCurrentFaviconUrl);
      } else {
        setCurrentFaviconUrl(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup Blob URLs au unmount (évite les fuites mémoire).
  useEffect(() => {
    return () => {
      if (currentLogoUrl) URL.revokeObjectURL(currentLogoUrl);
      if (currentFaviconUrl) URL.revokeObjectURL(currentFaviconUrl);
    };
  }, [currentLogoUrl, currentFaviconUrl]);

  // URL de preview résultante :
  //  - si un fichier pending → objet URL local
  //  - sinon si une suppression est pending → null (placeholder)
  //  - sinon → URL du Blob serveur courant
  const logoPreviewUrl = useMemo(() => {
    if (pendingLogo) return URL.createObjectURL(pendingLogo);
    if (logoDeletionPending) return null;
    return currentLogoUrl;
  }, [pendingLogo, logoDeletionPending, currentLogoUrl]);

  const faviconPreviewUrl = useMemo(() => {
    if (pendingFavicon) return URL.createObjectURL(pendingFavicon);
    if (faviconDeletionPending) return null;
    return currentFaviconUrl;
  }, [pendingFavicon, faviconDeletionPending, currentFaviconUrl]);

  // Cleanup des Object URLs de preview quand le pending change.
  useEffect(() => {
    return () => {
      if (pendingLogo && logoPreviewUrl && logoPreviewUrl.startsWith('blob:'))
        URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [pendingLogo, logoPreviewUrl]);
  useEffect(() => {
    return () => {
      if (pendingFavicon && faviconPreviewUrl && faviconPreviewUrl.startsWith('blob:'))
        URL.revokeObjectURL(faviconPreviewUrl);
    };
  }, [pendingFavicon, faviconPreviewUrl]);

  if (!isAdmin) {
    return (
      <Banner tone="warning">
        Section réservée aux administrateurs. Les directeurs n&apos;ont pas accès à l&apos;identité
        visuelle de l&apos;app.
      </Banner>
    );
  }

  if (profile === null && !error) {
    return <Skeleton className="h-[400px] w-full" />;
  }

  async function handleInseeLookup() {
    const cleaned = siret.replace(/\s|-/g, '');
    if (!/^\d{14}$/.test(cleaned)) {
      toast({ tone: 'warning', message: 'SIRET invalide (14 chiffres requis)' });
      return;
    }
    setInseeLoading(true);
    try {
      const res = await prospectsApi.insee(token, cleaned);
      if (!res.data) {
        toast({ tone: 'warning', message: 'SIRET introuvable dans INSEE' });
        return;
      }
      if (res.data.companyName && !name) setName(res.data.companyName);
      if (res.data.addressStreet) setStreet(res.data.addressStreet);
      if (res.data.addressCity) setCity(res.data.addressCity);
      if (res.data.addressPostalCode) setZip(res.data.addressPostalCode);
      toast({ tone: 'success', message: 'Adresse récupérée depuis INSEE' });
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'SIRET introuvable',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    } finally {
      setInseeLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      // 1) Suppressions pending (avant upload, sinon le POST repose le flag).
      if (logoDeletionPending && !pendingLogo) {
        await configApi.deleteOrganizationLogo(token);
      }
      if (faviconDeletionPending && !pendingFavicon) {
        await configApi.deleteOrganizationFavicon(token);
      }

      // 2) Uploads pending.
      if (pendingLogo) await configApi.uploadOrganizationLogo(token, pendingLogo);
      if (pendingFavicon) await configApi.uploadOrganizationFavicon(token, pendingFavicon);

      // 3) Patch profil (champs texte + couleur + alignement).
      const body: Partial<OrganizationProfile> = {};
      const cleanedSiret = siret.replace(/\s|-/g, '');
      const cleanedOrias = orias.replace(/\s|-/g, '');
      if (name.trim()) body.name = name.trim();
      if (cleanedSiret) body.siret = cleanedSiret;
      if (cleanedOrias) body.orias = cleanedOrias;
      body.address = {
        street: street.trim() || undefined,
        city: city.trim() || undefined,
        zip: zip.trim() || undefined,
        country: 'FR',
      };
      body.brandColor = brandColor;
      body.logoAlign = logoAlign;
      await configApi.updateOrganization(token, body);

      applyBrandColor(brandColor);
      toast({ tone: 'success', message: 'Profil entreprise enregistré' });
      await load();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Enregistrement impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    } finally {
      setSaving(false);
    }
  }

  async function handlePendingDelete(kind: 'logo' | 'favicon') {
    const ok = await confirm({
      title:
        kind === 'logo'
          ? 'Supprimer le logo au prochain enregistrement ?'
          : 'Supprimer le favicon au prochain enregistrement ?',
      message:
        'Le fichier actuel sera supprimé uniquement quand vous cliquerez sur « Enregistrer les modifications ».',
      variant: 'danger',
      confirmLabel: 'OK',
    });
    if (!ok) return;
    if (kind === 'logo') {
      setPendingLogo(null);
      setLogoDeletionPending(true);
    } else {
      setPendingFavicon(null);
      setFaviconDeletionPending(true);
    }
  }

  return (
    <div className="space-y-6">
      {error && <Banner tone="danger">{error}</Banner>}

      <Card className="space-y-4 p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
          Identité administrative
        </h3>
        <div>
          <Label htmlFor="org-name">Nom de l&apos;entreprise</Label>
          <Input
            id="org-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            placeholder="Adélie Assurances"
          />
        </div>

        <div>
          <Label htmlFor="org-siret">SIRET (14 chiffres)</Label>
          <div className="flex gap-2">
            <Input
              id="org-siret"
              value={siret}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d\s-]/g, '').slice(0, 17);
                setSiret(raw);
              }}
              placeholder="12345678900019"
              className="font-mono"
            />
            <Button
              type="button"
              variant="secondary"
              leadingIcon={<Search className="h-4 w-4" />}
              onClick={handleInseeLookup}
              loading={inseeLoading}
              disabled={!siret.trim()}
            >
              INSEE
            </Button>
          </div>
          <p className="mt-1 text-[11px] text-text-muted">
            Le bouton INSEE récupère automatiquement l&apos;adresse depuis le SIRET.
          </p>
        </div>

        <div>
          <Label htmlFor="org-orias">ORIAS (8 chiffres)</Label>
          <Input
            id="org-orias"
            value={orias}
            onChange={(e) => {
              const raw = e.target.value.replace(/\D/g, '').slice(0, 8);
              setOrias(raw);
            }}
            placeholder="07001234"
            className="font-mono"
            maxLength={8}
          />
        </div>

        <div>
          <Label htmlFor="org-street">Adresse</Label>
          <Input
            id="org-street"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            placeholder="12 rue de la Paix"
            maxLength={255}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="org-zip">Code postal</Label>
            <Input
              id="org-zip"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              placeholder="75002"
              maxLength={10}
            />
          </div>
          <div>
            <Label htmlFor="org-city">Ville</Label>
            <Input
              id="org-city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Paris"
              maxLength={100}
            />
          </div>
        </div>
      </Card>

      <Card className="space-y-4 p-6">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
          Identité visuelle
        </h3>

        <div>
          <Label htmlFor="org-color">Couleur de marque</Label>
          <div className="flex items-center gap-3">
            <input
              id="org-color"
              type="color"
              value={brandColor}
              onChange={(e) => setBrandColor(e.target.value.toUpperCase())}
              className="h-10 w-16 cursor-pointer rounded-md border border-border-subtle"
            />
            <Input
              value={brandColor}
              onChange={(e) => {
                const v = e.target.value.toUpperCase();
                if (/^#[0-9A-F]{0,6}$/.test(v)) setBrandColor(v);
              }}
              maxLength={7}
              className="w-32 font-mono"
            />
            <span className="text-xs text-text-muted">
              Appliquée immédiatement après enregistrement (overrides le violet par défaut).
            </span>
          </div>
        </div>

        <AssetUploader
          kind="logo"
          label="Logo principal (affiché dans la sidebar)"
          hint="PNG ou JPEG, max 200 Ko, ratio ~2:1 recommandé"
          previewUrl={logoPreviewUrl}
          hasPending={Boolean(pendingLogo) || logoDeletionPending}
          onPick={(file) => {
            setPendingLogo(file);
            setLogoDeletionPending(false);
          }}
          onRequestDelete={() => void handlePendingDelete('logo')}
          onCancelPending={() => {
            setPendingLogo(null);
            setLogoDeletionPending(false);
          }}
        />

        <div>
          <Label>Alignement du logo dans la sidebar</Label>
          <div className="border-border-subtle inline-flex overflow-hidden rounded-md border bg-white">
            {(['left', 'center'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setLogoAlign(v)}
                className={cn(
                  'px-4 py-1.5 text-xs font-medium transition-colors',
                  logoAlign === v ? 'bg-accent/10 text-accent' : 'text-text-muted hover:text-brand',
                )}
              >
                {v === 'left' ? 'Aligné à gauche' : 'Centré'}
              </button>
            ))}
          </div>
        </div>

        <AssetUploader
          kind="favicon"
          label="Favicon (icône onglet navigateur)"
          hint="PNG carré, max 50 Ko, 64×64 minimum recommandé"
          previewUrl={faviconPreviewUrl}
          hasPending={Boolean(pendingFavicon) || faviconDeletionPending}
          onPick={(file) => {
            setPendingFavicon(file);
            setFaviconDeletionPending(false);
          }}
          onRequestDelete={() => void handlePendingDelete('favicon')}
          onCancelPending={() => {
            setPendingFavicon(null);
            setFaviconDeletionPending(false);
          }}
        />
      </Card>

      <div className="flex justify-end">
        <Button variant="primary" onClick={handleSave} loading={saving}>
          Enregistrer les modifications
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Uploader asset avec preview
// ---------------------------------------------------------------------------

function AssetUploader({
  kind,
  label,
  hint,
  previewUrl,
  hasPending,
  onPick,
  onRequestDelete,
  onCancelPending,
}: {
  kind: 'logo' | 'favicon';
  label: string;
  hint: string;
  previewUrl: string | null;
  hasPending: boolean;
  onPick: (file: File) => void;
  onRequestDelete: () => void;
  onCancelPending: () => void;
}) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Garde-fou côté client (vérif stricte magic bytes côté serveur à l'upload).
    const accepted = kind === 'logo' ? ['image/png', 'image/jpeg'] : ['image/png'];
    if (!accepted.includes(file.type)) {
      toast({
        tone: 'danger',
        title: 'Format non supporté',
        message: kind === 'logo' ? 'PNG ou JPEG attendu' : 'PNG uniquement (favicon)',
      });
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    const maxBytes = kind === 'logo' ? 200 * 1024 : 50 * 1024;
    if (file.size > maxBytes) {
      toast({
        tone: 'danger',
        title: 'Fichier trop lourd',
        message: `Max ${maxBytes / 1024} Ko (fichier : ${Math.round(file.size / 1024)} Ko)`,
      });
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    onPick(file);
    if (inputRef.current) inputRef.current.value = '';
  }

  const previewBox = kind === 'favicon' ? 'h-16 w-16' : 'h-16 w-32';
  const previewBg =
    'bg-[repeating-conic-gradient(#f3f4f6_0_25%,#ffffff_0_50%)] bg-[length:12px_12px]';

  return (
    <div>
      <Label>{label}</Label>
      <div className="flex flex-wrap items-start gap-3">
        <div
          className={cn(
            'flex items-center justify-center overflow-hidden rounded-md border border-border-subtle',
            previewBox,
            previewBg,
          )}
        >
          {previewUrl ? (
            <img src={previewUrl} alt={label} className="h-full w-full object-contain" />
          ) : (
            <span className="text-[10px] text-text-muted">Aucun</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={kind === 'logo' ? 'image/png,image/jpeg' : 'image/png'}
            onChange={handleFile}
            className="hidden"
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leadingIcon={<Upload className="h-3.5 w-3.5" />}
            onClick={() => inputRef.current?.click()}
          >
            {previewUrl ? 'Remplacer' : 'Téléverser'}
          </Button>
          {previewUrl && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              leadingIcon={<Trash2 className="h-3.5 w-3.5" />}
              onClick={onRequestDelete}
            >
              Supprimer
            </Button>
          )}
          {hasPending && (
            <Button type="button" variant="ghost" size="sm" onClick={onCancelPending}>
              Annuler la modification
            </Button>
          )}
        </div>
      </div>
      <p className="mt-1 text-[11px] text-text-muted">{hint}</p>
      {hasPending && (
        <p className="mt-1 text-[11px] text-accent">
          ⓘ Changement en attente - sera appliqué au prochain enregistrement.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fetch asset depuis le backend en gardant le Bearer token
// ---------------------------------------------------------------------------

async function fetchAsset(kind: 'logo' | 'favicon', token: string): Promise<string | null> {
  try {
    const res = await fetch(`/api/v1/config/organization/${kind}`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}
